import { initializeApp, getApps, getApp } from 'firebase/app'
import { getMessaging, onMessage /* , isSupported as isMessagingSupported */ } from 'firebase/messaging'
import { GlobalEventEmitter } from '@/utils/GlobalEventEmitter'
import { FIREBASE as FIREBASE_RAW } from '@/config'

let appInstance = null
let messagingInstance = null
let foregroundUnsub = null
let warnedConfig = false

const REQUIRED = ['apiKey', 'projectId', 'messagingSenderId', 'appId']
const has = (o, k) => !!(o && o[k] && String(o[k]).trim())
const w = typeof window !== 'undefined' ? window : undefined
const DBG = () => !!(w && w.__APP_CONFIG__ && String(w.__APP_CONFIG__.MESSAGING_DEBUG || '') === '1')

// NEW: cheap, synchronous env probe (we could use isSupported(), but that’s async)
const envSupportsMessaging = () =>
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator

function resolveFirebaseConfig() {
  const cfg = {
    apiKey: FIREBASE_RAW?.apiKey || '',
    authDomain: FIREBASE_RAW?.authDomain || '',
    projectId: FIREBASE_RAW?.projectId || '',
    storageBucket: FIREBASE_RAW?.storageBucket || '',
    messagingSenderId: FIREBASE_RAW?.messagingSenderId || '',
    appId: FIREBASE_RAW?.appId || '',
    measurementId: FIREBASE_RAW?.measurementId || '',
  }
  const missing = REQUIRED.filter(k => !has(cfg, k))
  return { cfg, missing }
}

function getValidatedApp(cfg) {
  const apps = getApps()
  if (!apps.length) {
    const app = initializeApp(cfg)
    if (DBG()) console.log('[FB] init default app', app.name, app.options)
    return app
  }

  const defaultApp = getApp()
  const opts = defaultApp?.options || {}
  const bad = REQUIRED.some(k => !has(opts, k))

  if (!bad) {
    if (DBG()) console.log('[FB] using default app', defaultApp.name, defaultApp.options)
    return defaultApp
  }

  const NAME = 'app-messaging'
  try {
    const app = initializeApp(cfg, NAME)
    if (DBG()) console.log('[FB] init named app', app.name, app.options)
    return app
  } catch (_) {
    const app = getApp(NAME)
    if (DBG()) console.log('[FB] reuse named app', app.name, app.options)
    return app
  }
}

// Expose a tiny debug helper to the console (no imports needed)
if (typeof window !== 'undefined' && !window.__fbApps) {
  window.__fbApps = () => {
    try {
      const list = getApps().map(a => ({ name: a.name, options: a.options }))
      console.log('[FB] apps:', list)
      return list
    } catch (e) {
      console.error('[FB] apps inspect failed:', e)
      return []
    }
  }
}

export const firebase = () => {
  const { cfg, missing } = resolveFirebaseConfig()

  if (missing.length) {
    if (!warnedConfig) {
      console.error('[firebase] Missing config keys:', missing.join(', '))
      warnedConfig = true
    }
    const getMessage = () => () => {} // no-op unsubscribe
    return { getMessage, messaging: null }
  }

  // NEW: bail early on environments without SW/Notifications support
  if (!envSupportsMessaging()) {
    if (DBG()) console.log('[FB] messaging not supported by environment')
    const getMessage = () => () => {}
    return { getMessage, messaging: null }
  }

  appInstance = getValidatedApp(cfg)

  if (!messagingInstance) {
    try {
      messagingInstance = getMessaging(appInstance)
      if (DBG()) console.log('[FB] messaging bound to app', appInstance.name)
    } catch (e) {
      console.error('Firebase getMessaging failed:', e)
      messagingInstance = null
    }
  }

  const getMessage = () => {
    if (!messagingInstance) return () => {}
    if (foregroundUnsub) return () => {}
    foregroundUnsub = onMessage(messagingInstance, payload => {
      GlobalEventEmitter.$emit('newMessageReceived', payload)
    })
    return () => {
      try { foregroundUnsub?.() } finally { foregroundUnsub = null }
    }
  }

  return { getMessage, messaging: messagingInstance }
}
